import { useEffect, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { toast } from "@/components/ui/sonner";
import { useAuth } from '@/contexts/AuthContext';
import { useEdgeFunction } from '@/hooks/useEdgeFunction';
import { supabase } from '@/integrations/supabase/client';
import { ROUTES } from '@/lib/routes';
import { useReducerWithDevtools } from '@/lib/useReducerWithDevtools';
import { boardDetailReducer, initialBoardDetailState } from '@/reducers/boardDetailReducer';

export const useBoardDetailState = (boardId: string | undefined) => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { execute: invoke, isLoading: isInvoking } = useEdgeFunction({ auth: true });
  const [state, dispatch] = useReducerWithDevtools(
    boardDetailReducer,
    initialBoardDetailState,
    'BoardDetail'
  );

  // Fetch board data
  const fetchBoardData = useCallback(async () => {
    try {
      const { data: boardData } = await supabase
        .from('boards')
        .select('*')
        .eq('id', boardId)
        .single();
      
      if (boardData) {
        dispatch({ type: 'SET_BOARD', payload: boardData });
      }

      const { data: personasData } = await supabase
        .from('personas')
        .select('*')
        .eq('board_id', boardId);
      
      dispatch({ type: 'SET_PERSONAS', payload: personasData || [] });

      const { data: projectsData } = await supabase
        .from('projects')
        .select('*')
        .eq('board_id', boardId)
        .eq('archived', false)
        .order('created_at', { ascending: false });
      
      dispatch({ type: 'SET_PROJECTS', payload: projectsData || [] });
      
      // Fetch other boards for potential project migration
      if (boardData?.workspace_id) {
        const { data: boardsData } = await supabase
          .from('boards')
          .select('*')
          .eq('workspace_id', boardData.workspace_id)
          .neq('id', boardId);
        dispatch({ type: 'SET_AVAILABLE_BOARDS', payload: boardsData || [] });
      }
    } catch (error) {
      toast.error('Failed to load board');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [boardId]);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate(ROUTES.AUTH);
      return;
    }
    fetchBoardData();
  }, [boardId, user, navigate, fetchBoardData]);

  // Add persona
  const addPersona = useCallback(async () => {
    if (!state.newPersona.role_name || !state.newPersona.mission) {
      toast.error('Please fill in role name and mission');
      return;
    }

    const { error } = await supabase.from('personas').insert({
      board_id: boardId,
      ...state.newPersona,
    });

    if (error) {
      toast.error('Failed to add persona');
    } else {
      toast.success('Persona added');
      dispatch({ type: 'SET_IS_ADDING_PERSONA', payload: false });
      dispatch({ type: 'RESET_NEW_PERSONA' });
      fetchBoardData();
    }
  }, [state.newPersona, boardId, fetchBoardData]);

  // Delete persona
  const deletePersona = useCallback(async (personaId: string) => {
    const { error } = await supabase.from('personas').delete().eq('id', personaId);
    if (error) {
      toast.error('Failed to delete persona');
    } else {
      toast.success('Persona deleted');
      fetchBoardData();
    }
  }, [fetchBoardData]);

  // Add project
  const addProject = useCallback(async () => {
    if (!state.newProjectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    const { data, error } = await supabase
      .from('projects')
      .insert({ board_id: boardId, name: state.newProjectName })
      .select()
      .single();

    if (error) {
      toast.error('Failed to create project');
    } else {
      toast.success('Project created');
      dispatch({ type: 'SET_IS_ADDING_PROJECT', payload: false });
      dispatch({ type: 'SET_NEW_PROJECT_NAME', payload: '' });
      navigate(`/projects/${data.id}`);
    }
  }, [state.newProjectName, boardId, navigate]);

  // Name editing
  const startEditingName = useCallback(() => {
    dispatch({ type: 'SET_EDITED_NAME', payload: state.board?.name || '' });
    dispatch({ type: 'SET_IS_EDITING_NAME', payload: true });
  }, [state.board?.name]);

  const cancelEditingName = useCallback(() => {
    dispatch({ type: 'SET_IS_EDITING_NAME', payload: false });
    dispatch({ type: 'SET_EDITED_NAME', payload: '' });
  }, []);

  const saveEditedName = useCallback(async () => {
    if (!state.editedName.trim()) {
      toast.error('Board name cannot be empty');
      return;
    }

    const { error } = await supabase
      .from('boards')
      .update({ name: state.editedName.trim() })
      .eq('id', boardId);

    if (error) {
      toast.error('Failed to update board name');
    } else {
      toast.success('Board name updated');
      dispatch({ type: 'UPDATE_BOARD_NAME', payload: state.editedName.trim() });
      dispatch({ type: 'SET_IS_EDITING_NAME', payload: false });
    }
  }, [state.editedName, boardId]);

  // Description editing
  const startEditingDescription = useCallback(() => {
    dispatch({ type: 'SET_EDITED_DESCRIPTION', payload: state.board?.description || '' });
    dispatch({ type: 'SET_IS_EDITING_DESCRIPTION', payload: true });
  }, [state.board?.description]);

  const cancelEditingDescription = useCallback(() => {
    dispatch({ type: 'SET_IS_EDITING_DESCRIPTION', payload: false });
    dispatch({ type: 'SET_EDITED_DESCRIPTION', payload: '' });
  }, []);

  const saveEditedDescription = useCallback(async () => {
    const { error } = await supabase
      .from('boards')
      .update({ description: state.editedDescription.trim() || null })
      .eq('id', boardId);

    if (error) {
      toast.error('Failed to update description');
    } else {
      toast.success('Description updated');
      dispatch({ type: 'UPDATE_BOARD_DESCRIPTION', payload: state.editedDescription.trim() || null });
      dispatch({ type: 'SET_IS_EDITING_DESCRIPTION', payload: false });
    }
  }, [state.editedDescription, boardId]);

  // Color editing
  const startEditingColor = useCallback(() => {
    const oklchColor = state.board?.color || 'oklch(0.65 0.2 180)';
    dispatch({ type: 'SET_EDITED_COLOR', payload: oklchColor });
    dispatch({ type: 'SET_IS_EDITING_COLOR', payload: true });
  }, [state.board?.color]);

  const cancelEditingColor = useCallback(() => {
    dispatch({ type: 'SET_IS_EDITING_COLOR', payload: false });
    dispatch({ type: 'SET_EDITED_COLOR', payload: '' });
  }, []);

  const saveEditedColor = useCallback(async () => {
    if (!state.editedColor) return;
    
    const { error } = await supabase
      .from('boards')
      .update({ color: state.editedColor })
      .eq('id', boardId);

    if (error) {
      toast.error('Failed to update color');
    } else {
      toast.success('Color updated');
      dispatch({ type: 'UPDATE_BOARD_COLOR', payload: state.editedColor });
      dispatch({ type: 'SET_IS_EDITING_COLOR', payload: false });
    }
  }, [state.editedColor, boardId]);

  // Persona editing
  const startEditingPersona = useCallback((persona: any) => {
    dispatch({ type: 'SET_EDITING_PERSONA', payload: persona });
    dispatch({ type: 'SET_IS_EDITING_PERSONA', payload: true });
  }, []);

  const updatePersona = useCallback(async () => {
    if (!state.editingPersona?.role_name || !state.editingPersona?.mission) {
      toast.error('Please fill in role name and mission');
      return;
    }

    const { error } = await supabase
      .from('personas')
      .update({
        role_name: state.editingPersona.role_name,
        mission: state.editingPersona.mission,
        principles: state.editingPersona.principles,
        competencies: state.editingPersona.competencies,
        key_relationships: state.editingPersona.key_relationships,
        typical_questions: state.editingPersona.typical_questions,
      })
      .eq('id', state.editingPersona.id);

    if (error) {
      toast.error('Failed to update persona');
    } else {
      toast.success('Persona updated');
      dispatch({ type: 'SET_IS_EDITING_PERSONA', payload: false });
      dispatch({ type: 'SET_EDITING_PERSONA', payload: null });
      dispatch({ type: 'SET_GENERATED_PERSONA_DATA', payload: null });
      fetchBoardData();
    }
  }, [state.editingPersona, fetchBoardData]);

  // Regenerate persona
  const handleRegenerateClick = useCallback((persona: any) => {
    dispatch({ type: 'SET_REGENERATING_PERSONA', payload: persona });
    dispatch({ type: 'SET_REGENERATE_PROMPT', payload: '' });
    dispatch({ type: 'SET_IS_REGENERATE_DIALOG_OPEN', payload: true });
  }, []);

  const handleRegeneratePersona = useCallback(async () => {
    if (!state.regeneratePrompt.trim()) {
      toast.error("Please describe what you'd like to change");
      return;
    }

    const allRoles = state.personas.map(p => p.role_name);
    const context = state.board?.description || '';
    const changeRequest = `The user has requested the following changes to the ${state.regeneratingPersona?.role_name} persona: ${state.regeneratePrompt}`;

    const { data, error } = await invoke('generate-persona-details', {
      body: {
        roleName: state.regeneratingPersona?.role_name,
        problem: changeRequest,
        context,
        allRoles
      },
      showErrorToast: true,
    });

    if (error) return;

    dispatch({ type: 'SET_GENERATED_PERSONA_DATA', payload: data });
    dispatch({
      type: 'SET_EDITING_PERSONA',
      payload: {
        ...state.regeneratingPersona!,
        mission: data.mission,
        principles: data.principles,
        competencies: data.competencies,
        key_relationships: data.key_relationships,
        typical_questions: data.typical_questions
      }
    });
    
    dispatch({ type: 'SET_IS_REGENERATE_DIALOG_OPEN', payload: false });
    dispatch({ type: 'SET_IS_EDITING_PERSONA', payload: true });
    toast.success('Persona regenerated! Review and save or regenerate again.');
  }, [state.regeneratePrompt, state.personas, state.board, state.regeneratingPersona, invoke]);

  const handleRerollFromEdit = useCallback(() => {
    dispatch({ type: 'SET_IS_EDITING_PERSONA', payload: false });
    dispatch({ type: 'SET_IS_REGENERATE_DIALOG_OPEN', payload: true });
  }, []);

  // Delete board
  const handleDeleteBoard = useCallback(async () => {
    const { data: projectsData } = await supabase
      .from('projects')
      .select('*')
      .eq('board_id', boardId)
      .eq('archived', false);

    if (projectsData && projectsData.length > 0) {
      dispatch({ type: 'SET_PROJECTS_TO_HANDLE', payload: projectsData });
      dispatch({ type: 'SET_SHOW_DELETE_DIALOG', payload: true });
    } else {
      await executeDeleteBoard();
    }
  }, [boardId]);

  const executeDeleteBoard = useCallback(async () => {
    if (state.projectsToHandle.length > 0) {
      if (state.selectedDestinationBoard) {
        const { error } = await supabase
          .from('projects')
          .update({ board_id: state.selectedDestinationBoard })
          .eq('board_id', boardId);

        if (error) {
          toast.error('Failed to move projects');
          return;
        }
      } else {
        const { error } = await supabase
          .from('projects')
          .update({ board_id: null })
          .eq('board_id', boardId);

        if (error) {
          toast.error('Failed to orphan projects');
          return;
        }
      }
    }

    const { error: personasError } = await supabase
      .from('personas')
      .delete()
      .eq('board_id', boardId);

    if (personasError) {
      toast.error('Failed to delete board personas');
      return;
    }

    const { error: boardError } = await supabase
      .from('boards')
      .delete()
      .eq('id', boardId);

    if (boardError) {
      toast.error('Failed to delete board');
    } else {
      toast.success('Board deleted successfully');
      dispatch({ type: 'SET_SHOW_DELETE_DIALOG', payload: false });
      navigate(ROUTES.DASHBOARD);
    }
  }, [state.projectsToHandle, state.selectedDestinationBoard, boardId, navigate]);

  return {
    state,
    dispatch,
    isInvoking,
    addPersona,
    deletePersona,
    addProject,
    startEditingName,
    cancelEditingName,
    saveEditedName,
    startEditingDescription,
    cancelEditingDescription,
    saveEditedDescription,
    startEditingColor,
    cancelEditingColor,
    saveEditedColor,
    startEditingPersona,
    updatePersona,
    handleRegenerateClick,
    handleRegeneratePersona,
    handleRerollFromEdit,
    handleDeleteBoard,
    executeDeleteBoard,
  };
};
